import { SearchForm } from '@src/features/RiskAnalysis/components/forms/Search';
import { SearchResults } from '@src/features/RiskAnalysis/components/SearchResults';
import { useSearch } from '@src/features/RiskAnalysis/hooks/useSearch';
import { ErrorHandler } from '@src/shared/ui/components/ErrorHandler';
import { Loader } from '@src/shared/ui/components/Loader';
import { useAssets } from '@src/shared/ui/hooks/useAssets';
import { Grid } from '@uhg-abyss/web/ui/Grid';
import { Heading } from '@uhg-abyss/web/ui/Heading';
import { motion } from 'framer-motion';
import React, { useState } from 'react';

/**
 * Page: RiskAnalysis
 *
 * Screen designed to identify, assess, and mitigate potential risks, aiming to minimize negative impacts and
 * maximize opportunities. Leads to the action path wizard.
 *
 * @returns {Element}
 * @constructor
 */
export function RiskAnalysis() {
  const [searchFilters, setSearchFilters] = useState({});
  const [showSearchResults, setShowSearchResults] = useState(false);

  const { assets, isLoadingAssets } = useAssets([
    {
      apiKey: 'RiskRecord',
      queryKey: 'ListRiskCodes',
    },
    {
      apiKey: 'RiskRecord',
      queryKey: 'ListViews',
    },
    {
      apiKey: 'Tag',
      payload: { userDefined: true },
      queryKey: 'ListTags',
    },
  ]);
  const { count, fetch: fetchSearchResults, isLoading: isLoadingSearchResults, results } = useSearch();

  return (
    <ErrorHandler location="src/pages/private/Analysis/RiskAnalysis/RiskAnalysis.jsx">
      <motion.div
        animate="open"
        initial={{ opacity: 0 }}
        variants={{
          closed: { opacity: 0 },
          open: { opacity: 1 },
        }}
      >
        <Grid>
          <Grid.Col span={{ xs: '100%' }}>
            <Heading offset={0}>Risk Analysis</Heading>
          </Grid.Col>
          {isLoadingAssets ? (
            <Grid.Col span={{ xs: '100%' }}>
              <Loader verticalAlignment="start" />
            </Grid.Col>
          ) : (
            [
              <Grid.Col key="search-form" span={{ xs: '100%' }}>
                <SearchForm
                  assets={assets}
                  handleSearch={fetchSearchResults}
                  isLoadingSearchResults={isLoadingSearchResults}
                  searchFilters={searchFilters}
                  setSearchFilters={setSearchFilters}
                  setShowSearchResults={setShowSearchResults}
                />
              </Grid.Col>,
              <Grid.Col key="search-results" span={{ xs: '100%' }}>
                {isLoadingSearchResults ? (
                  <Loader verticalAlignment="start" />
                ) : (
                  <React.Fragment>
                    {showSearchResults === true && (
                      <SearchResults assets={assets} count={count} results={results} searchFilters={searchFilters} />
                    )}
                  </React.Fragment>
                )}
              </Grid.Col>,
            ]
          )}
        </Grid>
      </motion.div>
    </ErrorHandler>
  );
}
